import React, {RefObject, useCallback, useEffect, useState} from 'react';
import {List} from 'immutable';
import {
  Button,
  DropdownItem,
  Text,
  Icon,
  WindowSizeConsumer,
  MenuListItem
} from '@albert-io/atomic';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';

import './thememenu.scss';

export const STICKY_SIDEBAR_OFFSET = 400;
export const SCROLL_PADDING = 160;

export type ThemeRefs = {
  [themeId: string]: RefObject<any>;
};

export const ThemeMenu = ({
  initialTheme,
  themes,
  themeRefs
}: {
  initialTheme?: string;
  themes: List<any>; // GuideLevelModelV2
  themeRefs: ThemeRefs;
}) => {
  if (!themes || themes.size === 0) {
    return null;
  }

  useEffect(() => {
    if (initialTheme) {
      themeRefs[initialTheme].current.scrollIntoView();
    }
  }, [initialTheme, themeRefs]);

  return (
    <WindowSizeConsumer>
      {({breakpoints, viewportWidth}) => {
        const hideSidebar = viewportWidth <= breakpoints.s;
        if (hideSidebar) {
          return <ThemeDropdown themes={themes} themeRefs={themeRefs} />;
        }
        return <ThemeSidebar themes={themes} themeRefs={themeRefs} />;
      }}
    </WindowSizeConsumer>
  );
};

const ThemeSidebar = ({themes, themeRefs}: {themes: List<any>; themeRefs: ThemeRefs}) => {
  const [selectedThemeId, setSelectedThemeId] = useState(themes.first().getId());

  const handleScroll = useCallback(() => {
    const scrolledToTheme = Object.entries(themeRefs).reduce((selectedTheme, currentThemeRef) => {
      const [themeId, ref] = currentThemeRef;
      const refTop = ref.current.parentNode.getBoundingClientRect().top - SCROLL_PADDING;
      if (refTop <= 0) {
        return themeId;
      }
      return selectedTheme;
    }, selectedThemeId);
    setSelectedThemeId(scrolledToTheme);
  }, [selectedThemeId, themeRefs]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, {passive: true});
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return (
    <div id='study-guide__sidebar' className='study-guide__sidebar u-pad-b_2 u-pad-t_1'>
      <Text className='u-mar-b_2 u-mar-l_2' size='s'>
        Contents:
      </Text>
      <ul className='u-pad_0 u-mar_0'>
        {themes.map((theme) => (
          <MenuListItem
            className='study-guide__menu-item'
            key={theme.getId()}
            text={theme.getName()}
            as='li'
            onMenuItemClick={() => {
              const ref = themeRefs[theme.getId()];
              ref.current.scrollIntoView({behavior: 'instant'});
            }}
            clickable
            selected={theme.getId() === selectedThemeId}
          >
            <MenuListItem.Text className='u-font-weight_normal'>
              <MarkdownRendererV2 text={theme.getName()} />
            </MenuListItem.Text>
          </MenuListItem>
        ))}
      </ul>
    </div>
  );
};

const ThemeDropdown = ({
  themes,
  themeRefs
}: {
  themes: List<any>; // GuideLevelModelV2
  themeRefs: ThemeRefs;
}) => {
  const [showThemeList, setShowThemeList] = useState(false);
  return (
    <>
      <Button
        color='secondary'
        variant='outlined'
        className='study-guide-dropdown--mobile u-mar-b_1'
        onClick={() => {
          setShowThemeList(!showThemeList);
        }}
      >
        <Icon icon='list-ul' className='u-mar-r_1' />
        Jump To...
        <Icon icon='caret-down' className='u-mar-l_1' />
      </Button>
      {showThemeList && (
        <div className='study-guide__dropdown-list'>
          {themes.map((theme) => (
            <DropdownItem
              className='study-guide__dropdown-item'
              noWrap={false}
              key={theme.getId()}
              onClick={() => {
                const ref = themeRefs[theme.getId()];
                ref.current.scrollIntoView({behavior: 'instant'});
                setShowThemeList(false);
              }}
            >
              <Text>
                <MarkdownRendererV2 text={theme.getName()} />
              </Text>
            </DropdownItem>
          ))}
        </div>
      )}
    </>
  );
};
